import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DsBannerState, DsModule } from '@levelaccess/design-system';

import {
  OAUTH_ERROR_CODE_PARAM,
  OAUTH_ERROR_CODES,
  OAUTH_ERROR_REASON_PARAM,
  OAuthErrorPageCode,
  OAuthErrorReason,
} from '@shared/constants/oauth';
import { SharedCommonUtility } from '@shared/utils/common.utility';

import { SharedModule } from '../../../../shared/shared.module';
import { LoginCardComponent } from '../../login/login-card/login-card.component';

interface IErrorTexts {
  headingKey: string;
  messageKey: string;
  bannerState: DsBannerState;
}

const ERROR_TEXTS_BY_REASON: Record<OAuthErrorReason, IErrorTexts> = {
  [OAuthErrorReason.linkNotValid]: {
    headingKey: 'oauth_link_not_valid_heading',
    messageKey: 'oauth_link_not_valid',
    bannerState: DsBannerState.error,
  },
  [OAuthErrorReason.requestNotRecognized]: {
    headingKey: 'oauth_error_request_not_recognized_heading',
    messageKey: 'oauth_error_request_not_recognized',
    bannerState: DsBannerState.error,
  },
  [OAuthErrorReason.serverError]: {
    headingKey: 'oauth_error_server_error_heading',
    messageKey: 'oauth_error_server_error',
    bannerState: DsBannerState.error,
  },
  [OAuthErrorReason.declined]: {
    headingKey: 'oauth_error_declined_heading',
    messageKey: 'oauth_error_declined',
    bannerState: DsBannerState.info,
  },
  [OAuthErrorReason.generic]: {
    headingKey: 'oauth_error_generic_heading',
    messageKey: 'oauth_error_generic',
    bannerState: DsBannerState.error,
  },
};

/**
 * Where the authorization server sends the browser when a connection cannot continue and there is nowhere else to go
 * (for example an application that is not recognized). Only a fixed reason and an allowed error code arrive in the address;
 * the text is ours, and anything else in the address is ignored.
 */
@Component({
  selector: 'app-oauth-error',
  templateUrl: './oauth-error.component.html',
  standalone: true,
  imports: [SharedModule, DsModule, LoginCardComponent],
})
export class OAuthErrorComponent {
  public readonly texts: IErrorTexts;
  /** Shown so that support and testers can tell the cases apart. */
  public readonly errorCode: OAuthErrorPageCode | undefined;

  constructor(activatedRoute: ActivatedRoute) {
    const { queryParams } = activatedRoute.snapshot;
    const reason: string | undefined = queryParams[OAUTH_ERROR_REASON_PARAM];
    this.texts =
      SharedCommonUtility.notNullishOrEmpty(reason) && Object.hasOwn(ERROR_TEXTS_BY_REASON, reason)
        ? ERROR_TEXTS_BY_REASON[reason as OAuthErrorReason]
        : ERROR_TEXTS_BY_REASON[OAuthErrorReason.generic];
    this.errorCode = OAUTH_ERROR_CODES.find((code: OAuthErrorPageCode): boolean => code === queryParams[OAUTH_ERROR_CODE_PARAM]);
  }
}
